import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, Pencil, X, AlertCircle, CheckCircle2, History } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  myTimesheets, submitTimesheetCorrection, cancelTimesheetCorrection,
} from '../../../services/api';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

export default function MyTimesheetsHub() {
  const today = new Date();
  const prior = new Date(today); prior.setDate(prior.getDate() - 30);
  const [range, setRange] = useState({
    from: prior.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [correctingEntry, setCorrectingEntry] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows(await myTimesheets(range.from, range.to)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load timesheets'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [range.from, range.to]);

  const totalHours = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0), [rows]);

  const cancel = async (c) => {
    if (!window.confirm('Cancel this correction request?')) return;
    try { await cancelTimesheetCorrection(c.id); toast.success('Request cancelled'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="my-timesheets-hub">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
            <Clock className="h-3.5 w-3.5" />Lutrom Engineering
          </div>
          <h1 className="text-2xl font-display font-bold">My Timesheets</h1>
          <p className="text-sm text-slate-500 mt-1">Your own time entries. Request a correction if anything looks off — your admin will review.</p>
        </div>
        <Link to="/employee/hub" className="text-xs text-slate-500 hover:text-slate-700 mt-2">← Hub</Link>
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
        <label className="text-xs text-slate-600 flex items-center gap-2">
          From
          <input type="date" className="border rounded px-2 py-1 text-sm" value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            data-testid="my-timesheets-from" />
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-2">
          To
          <input type="date" className="border rounded px-2 py-1 text-sm" value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            data-testid="my-timesheets-to" />
        </label>
        <div className="ml-auto text-sm font-medium text-slate-700">
          {rows.length} entries · <span className="text-emerald-700">{totalHours.toFixed(2)} h</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="hidden sm:grid sm:grid-cols-[140px,110px,110px,80px,80px,1fr,110px] gap-3 px-4 py-2 bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
          <div>Date</div><div>Clock In</div><div>Clock Out</div><div>Break</div><div>Hours</div><div>Site / Notes</div><div className="text-right">Status</div>
        </div>
        {loading && <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">
            No timesheet entries in this range.
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {rows.map((r, i) => {
            const c = r.correction;
            const open = c && c.status === 'pending';
            const decided = c && c.status !== 'pending';
            return (
              <div key={r.id || i} className="px-4 py-3" data-testid={`my-timesheets-row-${i}`}>
                <div className="sm:grid sm:grid-cols-[140px,110px,110px,80px,80px,1fr,110px] gap-3 items-center">
                  <div className="font-medium text-slate-900 text-sm">{fmtDate(r.date)}</div>
                  <div className="text-sm text-slate-700">{fmtTime(r.clockIn)}</div>
                  <div className="text-sm text-slate-700">{fmtTime(r.clockOut)}</div>
                  <div className="text-sm text-slate-700">{r.breakMins || 0}m</div>
                  <div className="text-sm font-semibold text-slate-900">{(parseFloat(r.hours) || 0).toFixed(2)}</div>
                  <div className="text-xs text-slate-600">
                    <div className="truncate">{r.siteName || r.workOrderId || (r.kind === 'lutrom' ? 'Lutrom HQ' : '—')}</div>
                    {r.notes && <div className="italic text-slate-400 truncate">{r.notes}</div>}
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2 sm:mt-0">
                    {!c && r.clockOut && (
                      <button onClick={() => setCorrectingEntry(r)}
                        className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
                        data-testid={`my-timesheets-correct-${i}`}>
                        <Pencil className="h-3 w-3" />Request fix
                      </button>
                    )}
                    {open && (
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded inline-flex items-center gap-1">
                        <History className="h-3 w-3" />Pending
                      </span>
                    )}
                    {decided && c.status === 'approved' && (
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />Fixed
                      </span>
                    )}
                    {decided && c.status === 'rejected' && (
                      <span className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 rounded inline-flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />Rejected
                      </span>
                    )}
                  </div>
                </div>
                {open && (
                  <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-amber-900">You requested: {c.requestedClockIn ? fmtTime(c.requestedClockIn) : '—'} → {c.requestedClockOut ? fmtTime(c.requestedClockOut) : '—'} · break {c.requestedBreakMins ?? '—'}m</div>
                      <div className="text-amber-800 italic mt-0.5">“{c.reason}”</div>
                    </div>
                    <button onClick={() => cancel(c)} className="text-rose-600 hover:bg-rose-100 rounded p-1"
                      title="Cancel" data-testid={`my-timesheets-cancel-${i}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {decided && c.adminNote && (
                  <div className="mt-2 text-xs text-slate-500 italic">Admin: “{c.adminNote}”</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {correctingEntry && (
        <CorrectionDialog entry={correctingEntry} onClose={() => setCorrectingEntry(null)}
          onSaved={async () => { setCorrectingEntry(null); await load(); }} />
      )}
    </div>
  );
}

function CorrectionDialog({ entry, onClose, onSaved }) {
  const toLocalInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };
  const [form, setForm] = useState({
    requestedClockIn: toLocalInput(entry.clockIn),
    requestedClockOut: toLocalInput(entry.clockOut),
    requestedBreakMins: entry.breakMins || 0,
    reason: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason.trim()) { toast.error('Please explain what needs fixing'); return; }
    setBusy(true);
    try {
      await submitTimesheetCorrection({
        entryId: entry.id,
        requestedClockIn: form.requestedClockIn ? new Date(form.requestedClockIn).toISOString() : '',
        requestedClockOut: form.requestedClockOut ? new Date(form.requestedClockOut).toISOString() : '',
        requestedBreakMins: Number(form.requestedBreakMins) || 0,
        reason: form.reason.trim(),
      });
      toast.success('Correction request submitted — your admin will review');
      await onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Submission failed');
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white w-full sm:max-w-md sm:rounded-lg p-4 sm:p-6"
        data-testid="correction-dialog">
        <h2 className="font-display text-lg font-bold mb-1">Request a timesheet correction</h2>
        <p className="text-xs text-slate-500 mb-4">{fmtDate(entry.date)} · current {fmtTime(entry.clockIn)} → {fmtTime(entry.clockOut)}</p>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">New clock-in</span>
            <input type="datetime-local" className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.requestedClockIn}
              onChange={(e) => setForm((f) => ({ ...f, requestedClockIn: e.target.value }))}
              data-testid="correction-in" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">New clock-out</span>
            <input type="datetime-local" className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.requestedClockOut}
              onChange={(e) => setForm((f) => ({ ...f, requestedClockOut: e.target.value }))}
              data-testid="correction-out" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Break (minutes)</span>
            <input type="number" min={0} className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.requestedBreakMins}
              onChange={(e) => setForm((f) => ({ ...f, requestedBreakMins: e.target.value }))}
              data-testid="correction-break" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Reason *</span>
            <textarea rows={3} className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              data-testid="correction-reason" placeholder="e.g. Forgot to clock out — left site at 17:30" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <Button type="submit" disabled={busy} data-testid="correction-submit">
            {busy ? 'Sending…' : 'Submit request'}
          </Button>
        </div>
      </form>
    </div>
  );
}
